import { mergeOrganizationRecipients } from "src-next/modules/organization-patients";

import type { CrmPatientSummary } from "../types";

/**
 * Público do "Encontrar pacientes" (de qual lista os filtros partem).
 * - appointments: pacientes atendidos (comportamento original do dialog).
 * - consent: pacientes com consentimento.
 * - registered: clientes cadastrados (Person).
 * - all: os três juntos, sem repetir a mesma pessoa (no ecommerce, sem os
 *   atendidos).
 */
export type PatientFinderAudience =
  | "appointments"
  | "consent"
  | "registered"
  | "all";

/** "Todos" é sempre a primeira opção e já vem selecionado. */
export const DEFAULT_PATIENT_FINDER_AUDIENCE: PatientFinderAudience = "all";

/**
 * Públicos exibidos, na ordem dos botões.
 * Ecommerce não trabalha com consultas: "Pacientes atendidos" não aparece.
 */
export function getAvailablePatientFinderAudiences(
  isEcommerceOrg: boolean,
): PatientFinderAudience[] {
  return isEcommerceOrg
    ? ["all", "consent", "registered"]
    : ["all", "appointments", "consent", "registered"];
}

const AUDIENCES = new Set<PatientFinderAudience>([
  "appointments",
  "consent",
  "registered",
  "all",
]);

export function isPatientFinderAudience(
  value: string,
): value is PatientFinderAudience {
  return AUDIENCES.has(value as PatientFinderAudience);
}

export interface PatientFinderAudienceSources {
  appointmentPatients: CrmPatientSummary[];
  consentPatients: CrmPatientSummary[];
  registeredClients: CrmPatientSummary[];
}

/**
 * Lista base do público escolhido, antes dos filtros.
 * `includeAppointments: false` (ecommerce): os atendidos ficam fora de tudo,
 * inclusive de "Todos". O padrão `true` mantém o comportamento anterior.
 */
export function resolvePatientFinderAudiencePatients(
  audience: PatientFinderAudience,
  sources: PatientFinderAudienceSources,
  options: { includeAppointments?: boolean } = {},
): CrmPatientSummary[] {
  const includeAppointments = options.includeAppointments ?? true;
  if (audience === "consent") return sources.consentPatients;
  if (audience === "registered") return sources.registeredClients;
  if (audience === "all") {
    return mergeOrganizationRecipients({
      appointmentPatients: includeAppointments ? sources.appointmentPatients : [],
      consentPatients: sources.consentPatients,
      registeredClients: sources.registeredClients,
    });
  }
  // Por segurança: público escondido no ecommerce nunca devolve atendidos.
  return includeAppointments ? sources.appointmentPatients : [];
}

/**
 * Person não tem consulta. Com o filtro de consultas ligado, ele sai da
 * lista — senão o modo "não atendidos" traria todos os Persons.
 */
export function onlyPatientRecipients(
  patients: CrmPatientSummary[],
): CrmPatientSummary[] {
  return patients.filter((p) => p.recipientResourceType !== "Person");
}
